import { theme } from "../Theme"
import { SubStatus } from "./cleaningSubStatus"

export type CleaningNewStatus =
    | "cleaningUnassigned"
    | "companyPending"
    | "companyUnapproved"
    | "companyCleanerUnassigned"
    | "approvalUnapproved"
    | "approvalPending"
    | "cleaningPreparing"
    | "cleaningStarted"
    | "cleaningFinished"
    | "cleaningReported"
    | "cleaningDisabled"

/**
 * SubStatus を CleaningNewStatus に変換する
 * カレンダー表示で使用
 *
 * 注: SubStatus と CleaningNewStatus は同じ値セットを持つため、
 * この関数は主に型安全性のために存在する
 */
export const convertSubStatusToCleaningNewStatus = (
    subStatus: SubStatus
): CleaningNewStatus => {
    return subStatus
}

// マツリ側のステータス表示ラベル
const matsuriStatusLabels: Record<CleaningNewStatus, string> = {
    cleaningUnassigned: "未割り当て",
    companyPending: "承認待ち",
    companyUnapproved: "不承認",
    companyCleanerUnassigned: "清掃準備中",
    approvalUnapproved: "不承認",
    approvalPending: "承認待ち",
    cleaningPreparing: "清掃準備中",
    cleaningStarted: "開始済み",
    cleaningFinished: "清掃終了済み",
    cleaningReported: "清掃報告済み",
    cleaningDisabled: "無効化済み"
}

// 業者側のステータス表示ラベル
const companyStatusLabels: Record<CleaningNewStatus, string> = {
    cleaningUnassigned: "未割り当て",
    companyPending: "未受託",
    companyUnapproved: "不承認済み",
    companyCleanerUnassigned: "未割り当て",
    approvalUnapproved: "不承認",
    approvalPending: "承認待ち",
    cleaningPreparing: "清掃準備中",
    cleaningStarted: "開始済み",
    cleaningFinished: "清掃終了済み",
    cleaningReported: "清掃報告済み",
    cleaningDisabled: "無効化済み"
}

export const displayCleaningNewStatus = (
    status: CleaningNewStatus | undefined,
    isMatsuri = true,
    hasAssignedCompany = false
): string | undefined => {
    if (!status) return undefined

    // Matsuriユーザーかつ業者がアサインされている場合
    if (isMatsuri && hasAssignedCompany) {
        // approvalPending または approvalUnapproved の場合は「清掃準備中」に変更
        if (status === "approvalPending" || status === "approvalUnapproved") {
            return matsuriStatusLabels.cleaningPreparing // "清掃準備中"
        }
    }

    const labels = isMatsuri ? matsuriStatusLabels : companyStatusLabels
    return labels[status]
}

// マツリ側のステータス表示カラー
// 同じラベルで表示されるステータスは同じ色にする
const matsuriStatusColors: Record<CleaningNewStatus, string> = {
    cleaningUnassigned: theme.palette.intentions.unassigned.main, // 未割り当て
    companyPending: theme.palette.intentions.pending.main, // 承認待ち
    companyUnapproved: theme.palette.intentions.unassigned.main, // 不承認
    companyCleanerUnassigned: theme.palette.intentions.prepared.main, // 清掃準備中
    approvalUnapproved: theme.palette.intentions.unassigned.main, // 不承認
    approvalPending: theme.palette.intentions.pending.main, // 承認待ち
    cleaningPreparing: theme.palette.intentions.prepared.main, // 清掃準備中
    cleaningStarted: theme.palette.intentions.started.main, // 開始済み
    cleaningFinished: theme.palette.intentions.finished.main, // 清掃終了済み
    cleaningReported: theme.palette.intentions.reported.main, // 清掃報告済み
    cleaningDisabled: theme.palette.intentions.disabled.main // 無効化済み
}

// 業者側のステータス表示カラー
// ref: https://www.figma.com/file/7VhJ2vCaYKMRra2foQj06X/m2m-Cleaning?type=design&node-id=6406%3A42803&mode=design&t=d1TDNdOnqs7iXMeO-1
const companyStatusColors: Record<CleaningNewStatus, string> = {
    cleaningUnassigned: theme.palette.intentions.unassigned.main, // 未割り当て
    companyPending: theme.palette.intentions.pending.main, // 未受託
    companyUnapproved: theme.palette.intentions.unassigned.main, // 不承認済み
    companyCleanerUnassigned: theme.palette.intentions.unassigned.main, // 未割り当て
    approvalUnapproved: theme.palette.intentions.unassigned.main, // 不承認
    approvalPending: theme.palette.intentions.pending.main, // 承認待ち
    cleaningPreparing: theme.palette.intentions.prepared.main, // 清掃準備中
    cleaningStarted: theme.palette.intentions.started.main, // 開始済み
    cleaningFinished: theme.palette.intentions.finished.main, // 清掃終了済み
    cleaningReported: theme.palette.intentions.reported.main, // 清掃報告済み
    cleaningDisabled: theme.palette.intentions.disabled.main // 無効化済み
}

export const colorCleaningNewStatus = (
    status: CleaningNewStatus | undefined,
    isMatsuri = true,
    hasAssignedCompany = false
) => {
    if (!status) return undefined

    // Matsuriユーザーかつ業者がアサインされている場合
    if (isMatsuri && hasAssignedCompany) {
        // approvalPending または approvalUnapproved の場合は黄色に変更
        if (status === "approvalPending" || status === "approvalUnapproved") {
            return matsuriStatusColors.cleaningPreparing // "#FFE082" (黄色)
        }
    }

    const colors = isMatsuri ? matsuriStatusColors : companyStatusColors
    return colors[status]
}

export const sortCleaningNewStatus = (
    a?: CleaningNewStatus,
    b?: CleaningNewStatus
): number => {
    if (a === undefined && b === undefined) return 0
    if (a === undefined) return 1
    if (b === undefined) return -1

    const statusOrder = [
        "cleaningUnassigned",
        "companyPending",
        "companyUnapproved",
        "companyCleanerUnassigned",
        "approvalUnapproved",
        "approvalPending",
        "cleaningPreparing",
        "cleaningStarted",
        "cleaningFinished",
        "cleaningReported",
        "cleaningDisabled"
    ] as const
    const aIndex = statusOrder.indexOf(a)
    const bIndex = statusOrder.indexOf(b)
    if (aIndex === bIndex) return 0
    if (aIndex > bIndex) return -1
    return 1
}
